import { observable, decorate, computed } from 'mobx';
import _ from 'lodash';
import moment from 'moment';

import { authUserState } from './common';
import { checkRights, formatDate } from '../functions';
import { buttonTitles } from '../dictionary';

const productTypes = [
	{ Id: 1, Name: "Сырье" },
	{ Id: 2, Name: "Полуфабрикаты" },
	{ Id: 3, Name: "Готовая продукция" }
];

const writeOffStatuses = [
	{ Id: 1, Name: "Новое" },
	{ Id: 2, Name: "Отправлено" },
	{ Id: 3, Name: "Подтверждено" }
];

/** Состояние страницы создания заявки и обработки заявок на списание */
export class WriteOffRequestState {
	constructor() {
		this.ProductList = [];
		this.CancelReasonList = [];

		this.setRequest();
	}

	/** Право обрабатывать заявки и формировать списания */
	get CanManage() {
		return authUserState.User
			? checkRights("ManageWriteOff") || checkRights("AcceptWriteOff")
			: false;
	}

	/** Право подтверждать сформированные списания */
	get CanAccept() { return authUserState.User ? checkRights("AcceptWriteOff") : false; }

	/** Признак открытой смены текущего пользователя */
	get ShiftActive() { return authUserState.Shift.Active; }

	/**
	 * Возможность сохранить форму:
	 * - смена пользователя должна быть открыта;
	 * - в обоих режимах должен быть выбран склад;
	 * - в режиме создания заявки после выбора склада доступно сохранение;
	 * - в режиме обработки новое списание сохраняется с текущей датой;
	 * - существующее списание можно сохранять и с указанной датой.
	 */
	get CanSave() {
		return this.ShiftActive && this.WriteOff.StockId > 0 &&
			(!this.WriteOff.ProcessingMode || this.WriteOff.Date === "" || this.WriteOff.Id > 0);
	}

	/** Формирование доступных пользователю складов для элементов выбора */
	get Stocks() {
		return _.map(authUserState.Stocks || [], (stock) => ({
			Key: stock.Id,
			Value: stock.Name
		}));
	}

	/**
	 * Создание новой модели заявки или списания
	 * @param {Object} item - исходные данные модели
	 */
	setRequest(item = {}) {
		this.WriteOff = new WriteOff(_.extend({
			ProcessingMode: this.CanManage,
			StockId: this.Stocks.length === 1
				? this.Stocks[0].Key
				: -1
		}, item));
	}

	/**
	 * Формирование модели списания из ответа сервера
	 * @param {Object} writeOff - данные списания
	 */
	setWriteOff(writeOff) {
		this.WriteOff = new WriteOff(_.isUndefined(writeOff)
			? {
				ProcessingMode: this.WriteOff.ProcessingMode,
				StockId: this.WriteOff.StockId
			}
			: _.extend({}, writeOff, {
				ProcessingMode: this.WriteOff.ProcessingMode,
				StockId: writeOff.StockId > 0 ? writeOff.StockId : this.WriteOff.StockId
			}));
	}
}

decorate(WriteOffRequestState, {
	WriteOff: observable,
	ProductList: observable,
	CancelReasonList: observable,
	CanManage: computed,
	CanAccept: computed,
	ShiftActive: computed,
	CanSave: computed,
	Stocks: computed
});

/** Состояние страницы списка и карточки списаний */
export class WriteOffState {
	constructor() {
		this.IsSendRequest = false;
		this.Filter = new WriteOffFilter();
		this.WriteOffs = [];
		this.HasMore = false;
		
		this.ProductList = [];
		this.CancelReasonList = [];
		
		this.WriteOff = undefined;
	}

	/** Право редактировать списания или подтверждать их */
	get CanManage() {
		return authUserState.User
			? checkRights("ManageWriteOff") || checkRights("AcceptWriteOff")
			: false;
	}

	/** Право подтверждать списания и видеть финансовые данные */
	get CanAccept() {
		return authUserState.User ? checkRights("AcceptWriteOff") : false;
	}

	/** Формирование доступных пользователю складов для элементов выбора */
	get Stocks() {
		return _.map(authUserState.allStocks || [], (stock) => ({
			Key: stock.Id,
			Value: stock.Name
		}));
	}

	/** Формирование списка статусов без статуса новой заявки */
	get Statuses() {
		return _.filter(writeOffStatuses, (status) => status.Id !== 1);
	}

	/** Показ суммы в подтвержденном списании пользователю с правом подтверждения */
	get ShowAmount() {
		return !_.isUndefined(this.WriteOff) && this.WriteOff.Status === 3 && this.CanAccept;
	}

	/** Формирование заголовка карточки с учетом статуса списания */
	get PageTitle() {
		return _.isUndefined(this.WriteOff)
			? ""
			: buttonTitles[(this.WriteOff.Status === 3 ? "View" : "Edit") + "WriteOff"];
	}

	/** Формирование набора колонок таблицы с учетом прав пользователя */
	get columns() {
		return {
			ShiftDate: {},
			User: {},
			Stock: {},
			Status: {
				filterable: false,
				accessor: "StatusName"
			},
			...(this.CanAccept ? { Amount: { width: 100 } } : {}),
			...(this.CanManage ? { View: {}, Edit: {} } : {})
		};
	}

	/**
	 * Разбор ответа списка списаний и подготовка данных строк таблицы
	 * @param {Object} data ответ API со списком списаний
	 */
	setWriteOffs(data) {
		this.WriteOffs = data.Success
			? _.map(data.WriteOffs, (writeOff) => ({
				...writeOff,
				StatusName: (_.find(writeOffStatuses, { Id: writeOff.Status }) || {}).Name || "",
				RowClass: writeOff.Status === 3 ? "notactive" : writeOff.AutoSend ? "notSend" : "",
				CanEdit: writeOff.Status !== 3,
				CanView: writeOff.Status === 3
			}))
			: [];
		this.HasMore = data.HasMore;
		this.IsSendRequest = true;
	}

	/**
	 * Подключение общей модели выбранного списания
	 * @param {Object|undefined} writeOff данные списания или undefined для возврата к списку
	 */
	setWriteOff(writeOff) {
		this.WriteOff = _.isUndefined(writeOff)
			? undefined
			: new WriteOff(writeOff.Id === -1 ? { Id: -1, ProcessingMode: true } :
				_.extend({}, writeOff, { ProcessingMode: true }));
	}
}

decorate(WriteOffState, {
	WriteOffs: observable,
	IsSendRequest: observable,
	HasMore: observable,
	Filter: observable,
	ProductList: observable,
	CancelReasonList: observable,
	WriteOff: observable,
	CanManage: computed,
	CanAccept: computed,
	Stocks: computed,
	Statuses: computed,
	ShowAmount: computed,
	PageTitle: computed,
	columns: computed
});

/** Модель фильтра списка списаний */
export class WriteOffFilter {
	constructor() {
		this.StockId = -1;
		this.UserId = -1;
		// this.ShiftDate = "";
		this.DateFrom = formatDate(moment().subtract(1, "month"), "r");
		this.DateTo = formatDate(moment(), "r");
		this.Status = -1;
		this.Limit = 100;
		this.Offset = 0;
	}

	/**
	 * Сбор значения фильтра
	 * @param {string} id имя поля фильтра
	 * @param {string|number} value значение поля фильтра
	 */
	collect(id, value) { this[id] = value; }

	/**
	 * Формирование чистых данных фильтра для API
	 * @returns {{ StockId : number, UserId : number, DateFrom : string, DateTo : string,
	 * Status : number, Limit : number, Offset : number }} параметры фильтра
	 */
	getData() {
		return _.omitBy({
			StockId: this.StockId,
			UserId: this.UserId,
			DateFrom: this.DateFrom,
			DateTo: this.DateTo,
			Status: this.Status,
			Limit: this.Limit,
			Offset: this.Offset
		}, (value) => value === "" || value === false || value === -1);
	}
}

decorate(WriteOffFilter, {
	StockId: observable,
	UserId: observable,
	// ShiftDate: observable,
	DateFrom: observable,
	DateTo: observable,
	Status: observable,
	Limit: observable,
	Offset: observable
});

/** Модель заявки и формируемого списания */
export class WriteOff {
	constructor(item = {}) {
		this.ProcessingMode = item.ProcessingMode || false;
		this.Id = _.isUndefined(item.Id) ? -1 : item.Id;
		this.Status = _.isUndefined(item.Status) ? -1 : item.Status;
		this.AutoSend = item.AutoSend || false;
		this.Date = item.Date || "";
		this.StockId = _.isUndefined(item.StockId) ? -1 : item.StockId;
		this.User = item.User || "";
		this.Stock = item.Stock || "";
		this.ShiftDate = item.ShiftDate || "";
		this.SendUser = item.SendUser || "";
		this.SendDate = item.SendDate || "";
		this.AcceptUser = item.AcceptUser || "";
		this.AcceptDate = item.AcceptDate || "";
		this.Amount = _.isUndefined(item.Amount) ? 0 : item.Amount;
		this.RequestIds = item.RequestIds || [];
		this.Products = _.map(productTypes, (type) => {
			var products = _.sortBy(
				_.filter(item.Products, { Type: type.Id }),
				(product) => product.CancelReasonId === -1 || product.CancelReasonId === ""
					? Infinity
					: parseInt(product.CancelReasonId, 10)
			);

			return {
				Id: type.Id,
				Name: type.Name,
				List: _.map(products.length ? products : [{}], (product) => new Product(product))
			};
		});

		this.ErrorCode = "";
		this.ButtonLoading = false;
		this.SuccessSave = false;
	}

	/** Готовность режима обработки к загрузке и сохранению списания */
	get ProcessingReady() { return this.ProcessingMode && this.StockId > 0; }

	/**
	 * Сбор значения поля модели
	 * @param {string} id - имя поля
	 * @param {*} value - новое значение
	 */
	collect(id, value) { this[id] = value; }

	/**
	 * Добавление товара в указанную группу
	 * @param {number} typeId - идентификатор типа товара
	 */
	addProduct(typeId) {
		var group = _.find(this.Products, { Id: parseInt(typeId, 10) });
		if (group) group.List = _.concat(group.List, new Product());
	}

	/**
	 * Удаление товара из указанной группы
	 * @param {number} typeId - идентификатор типа товара
	 * @param {number} index - индекс товара в группе
	 */
	removeProduct(typeId, index) {
		var group = _.find(this.Products, { Id: parseInt(typeId, 10) });
		if (group) group.List = _.filter(group.List, (product, productIndex) => productIndex !== index);
	}

	/**
	 * Добавление в модель товаров из ожидающих заявок
	 * @param {Array} requests - список заявок выбранного склада
	 */
	setProductsFromRequests(requests) {
		this.RequestIds = _.map(requests, "Id");

		var requestProducts = _.flatten(_.map(requests || [],
			(request) => request && request.Products ? request.Products : []));

		this.Products = _.map(this.Products, (group) => {
			var writeOffProducts = _.filter(group.List, (product) =>
					!product.New && product.ProductId !== "" && product.ProductId !== -1),
				newProducts = _.map(
					_.filter(requestProducts,
						(product) => product && parseInt(product.Type, 10) === group.Id),
					(product) => new Product(_.extend({}, product, { New: true }))
				),
				products = _.sortBy(
					_.concat(writeOffProducts, newProducts),
					(product) => product.CancelReasonId === -1 || product.CancelReasonId === ""
						? Infinity
						: parseInt(product.CancelReasonId, 10)
				);

			return _.extend({}, group, {
				List: products.length ? products : [new Product()]
			});
		});
	}

	/**
	 * Проверка данных заявки или списания перед сохранением:
	 * - выбран склад;
	 * - добавлен хотя бы один товар;
	 * - у каждого добавленного товара выбран продукт;
	 * - у каждого добавленного товара указано количество;
	 * - у каждого добавленного товара выбрана причина списания.
	 * @returns {boolean} результат проверки
	 */
	validate() {
		this.ErrorCode = "";

		var isValid = true,
			products = _.filter(_.flatMap(this.Products, "List"),
				(product) => product.ProductId !== "" && product.ProductId !== -1);

		if (this.StockId === "" || this.StockId === -1) {
			this.ErrorCode = "STOCK_REQUIRED";
			return false;
		}

		if (_.isEmpty(products)) {
			this.ErrorCode = "AUDIT_PRODUCTS_REQUIRED";
			return false;
		}

		_.each(products,  (product) => {
			if (product.Count === "" || isNaN(Number(product.Count)) || Number(product.Count) <= 0) {
				this.ErrorCode = "COUNT_REQUIRED";
				isValid = false;
				return false;
			}

			if (product.CancelReasonId === -1 || product.CancelReasonId === "") {
				this.ErrorCode = "CANCELREASON_REQUIRED";
				isValid = false;
				return false;
			}
		});

		return isValid;
	}

	/**
	 * Формирование данных заявки или списания для отправки
	 * @returns {Object} параметры API-запроса
	 */
	getData() {
		var data = {
				StockId: this.StockId
			},
			products = _.map(
				_.filter(_.flatMap(this.Products, "List"),
					(product) => product.ProductId !== "" && product.ProductId !== -1),
				(product) => product.getData()
			),
			index = 0;

		if (this.ProcessingMode) {
			data.Id = this.Id > 0 ? this.Id : -1;
			if (this.Date !== "") data.Date = moment(this.Date).format("YYYY-MM-DD HH:mm");
			if (!_.isEmpty(this.RequestIds)) data.Requests = this.RequestIds.join(",");
		}

		_.each(products, function (product) {
			var name = "Products." + index + ".";

			_.each(product, function(value, field) {
				data[name + field] = value;
			});
			index++;
		});

		return data;
	}
}

/** Товар заявки на списание */
export class Product {
	constructor(item = {}) {
		this.Type = _.isUndefined(item.Type) ? -1 : item.Type;
		this.ProductId = _.isUndefined(item.ProductId) ? -1 : item.ProductId;
		this.Product = item.Product || "";
		this.Count = _.isUndefined(item.Count) ? "" : item.Count;
		this.UnitName = item.UnitName || "";
		this.CancelReasonId = _.isUndefined(item.CancelReasonId) ? -1 : item.CancelReasonId;
		this.CancelReason = item.CancelReason || "";
		this.Description = item.Description || "";
		this.Amount = _.isUndefined(item.Amount) ? 0 : item.Amount;
		this.New = item.New || false;
	}

	/** Сбор данных строки товара */
	collect(id, value) { this[id] = value; }

	/** Чистые данные товара заявки */
	getData() {
		return {
			ProductId: this.ProductId,
			Count: this.Count,
			CancelReasonId: this.CancelReasonId,
			Description: this.Description
		};
	}
}

decorate(WriteOff, {
	ProcessingMode: observable,
	Id: observable,
	Status: observable,
	AutoSend: observable,
	Date: observable,
	StockId: observable,
	RequestIds: observable,
	ProcessingReady: computed,
	Products: observable,
	ButtonLoading: observable,
	ErrorCode: observable,
	SuccessSave: observable
});

decorate(Product, {
	Type: observable,
	ProductId: observable,
	Product: observable,
	Count: observable,
	UnitName: observable,
	CancelReasonId: observable,
	CancelReason: observable,
	Description: observable,
	Amount: observable,
	New: observable
});
